import { useState, useEffect } from 'react'
import { 
  Users, 
  UserPlus, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  RefreshCw, 
  Mail,
  Building2,
  Clock,
  PlayCircle,
  Eye
} from 'lucide-react'

/**
 * Consultant Provisioning Tool
 * 
 * Allows admins to create user accounts for all consultants
 * that have been used in previous assessment forms but don't have accounts yet.
 */
const ConsultantProvisioningTool = () => {
  const [summary, setSummary] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isProvisioning, setIsProvisioning] = useState(false)
  const [error, setError] = useState(null)
  const [provisioningResults, setProvisioningResults] = useState(null)
  const [expandedSection, setExpandedSection] = useState(null)

  // Get authorization token
  const getAuthToken = () => localStorage.getItem('adminToken')

  // Fetch consultant summary
  const fetchSummary = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await fetch('/api/admin/utilities/consultant-summary', {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      })
      
      const data = await response.json()
      
      if (data.success) {
        setSummary(data.data)
      } else {
        setError(data.error?.message || data.error || 'Erreur lors de la récupération du résumé')
      }
    } catch (err) {
      setError('Erreur réseau: ' + err.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Run provisioning
  const runProvisioning = async (dryRun = false) => {
    setIsProvisioning(true)
    setError(null)
    setProvisioningResults(null)
    
    try {
      const response = await fetch('/api/admin/utilities/provision-consultants', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({ dryRun })
      })
      
      const data = await response.json()
      
      if (data.success) {
        setProvisioningResults(data.data)
        // Refresh summary after provisioning
        if (!dryRun) {
          await fetchSummary()
        }
      } else {
        setError(data.error?.message || data.error || 'Erreur lors du provisionnement')
      }
    } catch (err) {
      setError('Erreur réseau: ' + err.message)
    } finally {
      setIsProvisioning(false)
    }
  }

  // Load summary on mount
  useEffect(() => {
    fetchSummary()
  }, [])

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section)
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Provisionnement des Consultants
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Créer les comptes pour les consultants des évaluations passées
          </p>
        </div>
        <button
          onClick={fetchSummary}
          disabled={isLoading}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-red-800">Erreur</h4>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && !summary && (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Chargement du résumé...</p>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Total Consultants */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-sm">Total</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {summary.total}
              </div>
              <div className="text-xs text-gray-500">consultants uniques</div>
            </div>

            {/* Existing Accounts */}
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Comptes existants</span>
              </div>
              <div className="text-2xl font-bold text-green-700">
                {summary.existing.length}
              </div>
              <div className="text-xs text-green-600">déjà créés</div>
            </div>

            {/* Missing Accounts */}
            <div className="bg-amber-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-amber-600 mb-1">
                <UserPlus className="w-4 h-4" />
                <span className="text-sm">Comptes manquants</span>
              </div>
              <div className="text-2xl font-bold text-amber-700">
                {summary.missing.length}
              </div>
              <div className="text-xs text-amber-600">à créer</div>
            </div>

            {/* Invalid Emails */}
            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm">Emails invalides</span>
              </div>
              <div className="text-2xl font-bold text-red-700">
                {summary.invalid.length}
              </div>
              <div className="text-xs text-red-600">non-CEDEC ou invalides</div>
            </div>
          </div>

          {/* Action Buttons */}
          {summary.missing.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 mb-1">
                  {summary.missing.length} compte(s) à créer
                </h4>
                <p className="text-sm text-blue-700">
                  Chaque consultant recevra un email avec un lien pour définir son mot de passe.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => runProvisioning(true)}
                  disabled={isProvisioning}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  <Eye className="w-4 h-4" />
                  Aperçu
                </button>
                <button
                  onClick={() => runProvisioning(false)}
                  disabled={isProvisioning}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isProvisioning ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <PlayCircle className="w-4 h-4" />
                  )}
                  Créer les comptes
                </button>
              </div>
            </div>
          )}

          {/* Provisioning Results */}
          {provisioningResults && (
            <div className={`mb-6 p-4 rounded-lg border ${provisioningResults.dryRun ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
              <h4 className={`font-medium mb-3 flex items-center gap-2 ${provisioningResults.dryRun ? 'text-blue-900' : 'text-green-900'}`}>
                {provisioningResults.dryRun ? (
                  <>
                    <Eye className="w-5 h-5" />
                    Aperçu (Dry Run)
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Provisionnement terminé
                  </>
                )}
              </h4>
              
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div className="text-center p-2 bg-white rounded">
                  <div className="text-lg font-bold text-green-600">{provisioningResults.created?.length || 0}</div>
                  <div className="text-xs text-gray-600">{provisioningResults.dryRun ? 'À créer' : 'Créés'}</div>
                </div>
                <div className="text-center p-2 bg-white rounded">
                  <div className="text-lg font-bold text-gray-600">{provisioningResults.skipped?.length || 0}</div>
                  <div className="text-xs text-gray-600">Ignorés</div>
                </div>
                <div className="text-center p-2 bg-white rounded">
                  <div className="text-lg font-bold text-red-600">{provisioningResults.failed?.length || 0}</div>
                  <div className="text-xs text-gray-600">Échoués</div>
                </div>
              </div>
              
              {provisioningResults.duration && (
                <div className="text-xs text-gray-600 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Durée: {Math.round(provisioningResults.duration / 1000)}s
                </div>
              )}
            </div>
          )}

          {/* Consultant Lists */}
          <div className="space-y-4">
            {/* Missing Consultants */}
            {summary.missing.length > 0 && (
              <div className="border rounded-lg">
                <button
                  onClick={() => toggleSection('missing')}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-gray-900">
                      Comptes à créer ({summary.missing.length})
                    </span>
                  </div>
                  <span className={`transform transition-transform ${expandedSection === 'missing' ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                
                {expandedSection === 'missing' && (
                  <div className="border-t max-h-64 overflow-y-auto">
                    {summary.missing.map((consultant, idx) => (
                      <div key={idx} className="p-3 border-b last:border-b-0 hover:bg-gray-50">
                        <div className="flex items-center gap-2 mb-1">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{consultant.email}</span>
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                            {consultant.assessmentCount} évaluation(s)
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-4 ml-6">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {consultant.companies?.slice(0, 3).join(', ')}
                            {consultant.companies?.length > 3 && `... +${consultant.companies.length - 3}`}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(consultant.firstAssessment)} - {formatDate(consultant.lastAssessment)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Existing Consultants */}
            {summary.existing.length > 0 && (
              <div className="border rounded-lg">
                <button
                  onClick={() => toggleSection('existing')}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-gray-900">
                      Comptes existants ({summary.existing.length})
                    </span>
                  </div>
                  <span className={`transform transition-transform ${expandedSection === 'existing' ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                
                {expandedSection === 'existing' && (
                  <div className="border-t max-h-64 overflow-y-auto">
                    {summary.existing.map((consultant, idx) => (
                      <div key={idx} className="p-3 border-b last:border-b-0 hover:bg-gray-50">
                        <div className="flex items-center gap-2 mb-1">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{consultant.email}</span>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            {consultant.assessmentCount} évaluation(s)
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-4 ml-6">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {consultant.companies?.slice(0, 3).join(', ')}
                            {consultant.companies?.length > 3 && `... +${consultant.companies.length - 3}`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Invalid Emails */}
            {summary.invalid.length > 0 && (
              <div className="border border-red-200 rounded-lg">
                <button
                  onClick={() => toggleSection('invalid')}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-red-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-gray-900">
                      Emails invalides ({summary.invalid.length})
                    </span>
                  </div>
                  <span className={`transform transition-transform ${expandedSection === 'invalid' ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                
                {expandedSection === 'invalid' && (
                  <div className="border-t border-red-200 max-h-64 overflow-y-auto">
                    {summary.invalid.map((consultant, idx) => (
                      <div key={idx} className="p-3 border-b border-red-100 last:border-b-0 hover:bg-red-50">
                        <div className="flex items-center gap-2 mb-1">
                          <Mail className="w-4 h-4 text-red-400" />
                          <span className="font-medium text-gray-900">{consultant.email}</span>
                        </div>
                        <div className="text-xs text-red-600 ml-6">
                          {consultant.reason}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* All accounts exist message */}
          {summary.missing.length === 0 && summary.existing.length > 0 && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <h4 className="font-medium text-green-800">Tous les comptes sont créés</h4>
                <p className="text-sm text-green-700">
                  Tous les consultants CEDEC des évaluations passées ont déjà un compte.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ConsultantProvisioningTool

